import React, { Fragment, useEffect, useState } from 'react';

import axios from 'axios';
import CreatableSelect from 'react-select/creatable';

import NumberFormat from 'CommonComponents/LocalisedNumberFormat';

export default function ExpenseQuantityEdit(props) {
    const [unlinking, setUnlinking] = useState(false);

    const changeAmount = (values) => {
        const { value } = values;
        props.changeAmountValue(props.index, 'amount', value);
    };

    const changeName = (e) => {
        const val = e.value;
        props.changeAmountValue(props.index, 'name', val);
    };

    const linkEnabled = typeof window.quantityLinkEnabled !== 'undefined' && window.quantityLinkEnabled;

    const slotLinkState = (() => {
        const slotStates = window.quantityLinkSlotStates;
        if (slotStates && slotStates[props.index] !== undefined) {
            return slotStates[props.index];
        }
        return null;
    })();

    const isLinked = linkEnabled && slotLinkState && slotLinkState.source_type && !slotLinkState.broken;
    const isBrokenLink = linkEnabled && slotLinkState && slotLinkState.broken;
    const sourceLabel = slotLinkState?.meta?.label || '';

    const openPicker = () => {
        window.dispatchEvent(
            new CustomEvent('quantitylink:open', {
                detail: {
                    expenseId: window.quantityLinkExpenseId,
                    sourcesUrl: window.quantityLinkSourcesUrl,
                    linkUrl: window.quantityLinkLinkUrl,
                    unlinkUrl: window.quantityLinkUnlinkUrl,
                    amountSlotIndex: props.index,
                    currentLink: slotLinkState
                        ? { sourceType: slotLinkState.source_type, sourceId: slotLinkState.source_id, driver: slotLinkState.meta?.driver ?? null }
                        : null,
                },
            }),
        );
    };

    const handleOpenPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openPicker();
    };

    // Auto-open the picker when arriving from the create form with a pending link slot
    useEffect(() => {
        if (linkEnabled && typeof window.quantityLinkAutoOpenSlot !== 'undefined' && window.quantityLinkAutoOpenSlot === props.index) {
            // Clear the flag so only the matching slot auto-opens once
            delete window.quantityLinkAutoOpenSlot;
            // Delay slightly to allow QuantityLinkApp to finish mounting
            const timer = setTimeout(() => openPicker(), 200);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleUnlink = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!confirm($t('Are you sure you want to unlink this quantity? The current value will be frozen.'))) {
            return;
        }
        setUnlinking(true);
        // Pass the slot index so the server unlinks only this specific slot
        const unlinkUrl = `${window.quantityLinkUnlinkUrl}?slot_index=${props.index}`;
        axios
            .delete(unlinkUrl)
            .then(() => {
                window.dispatchEvent(new CustomEvent('quantitylink:changed'));
            })
            .catch(() => {
                alert($t('Failed to unlink. Please try again.'));
            })
            .finally(() => setUnlinking(false));
    };

    // prettier-ignore
    const linkSvg = (
        <svg className="tw-w-4 tw-h-4 tw-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
        </svg>
    );

    return (
        <Fragment key={props.index}>
            <td>
                <div className="tw-max-w-xs">
                    <div className="tw-flex tw-items-center tw-gap-1">
                        <div className="tw-relative tw-flex-1">
                            <NumberFormat
                                thousandSeparator={true}
                                inputMode="numeric"
                                className={`tw-w-full tw-rounded-lg !tw-bg-white tw-text-input-text tw-text-xs tw-font-semibold tw-border-0 remove-comma ${
                                    props.errors &&
                                    props.errors.uncommitted_amount &&
                                    props.errors.uncommitted_amount[props.index] &&
                                    props.errors.uncommitted_amount[props.index].amount
                                        ? 'is-invalid'
                                        : ''
                                } ${isLinked ? '!tw-bg-blue-15 !tw-border !tw-border-blue' : ''} ${linkEnabled ? 'tw-pr-10' : ''}`}
                                data-name={props.helper ? `data-name="uncommitted_amount[${props.index}][amount]"` : ''}
                                name={!props.helper ? `uncommitted_amount[${props.index}][amount]` : ''}
                                value={`${props.name ? props.amount : ''}`}
                                onValueChange={changeAmount}
                                // Linked slots are read-only (not disabled) so the
                                // source-driven value is still submitted on save.
                                // Disabled inputs are omitted from the form, which
                                // dropped uncommitted_amount[N][amount] on update.
                                readOnly={!props.canBeEdited || isLinked}
                                disabled={props.expenseReadonly}
                                allowNegative={false}
                            />
                            {linkEnabled && props.canBeEdited && (
                                <button
                                    type="button"
                                    className="tw-absolute tw-right-1 tw-top-1/2 -tw-translate-y-1/2 tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-lg tw-bg-blue tw-text-white hover:tw-bg-blue-75 tw-transition-colors tw-shrink-0"
                                    title={$t('Link quantity to an embedded source')}
                                    aria-label={$t('Link quantity to an embedded source')}
                                    onClick={handleOpenPicker}
                                >
                                    {linkSvg}
                                </button>
                            )}
                        </div>
                        {isLinked && props.canBeEdited && (
                            <button
                                type="button"
                                className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-lg tw-text-red-500 hover:tw-text-white hover:tw-bg-red-500 tw-transition-colors tw-shrink-0"
                                title={$t('Unlink quantity from source. The current value will be frozen.')}
                                aria-label={$t('Unlink quantity from source')}
                                onClick={handleUnlink}
                                disabled={unlinking}
                            >
                                {/* prettier-ignore */}
                                <svg className="tw-w-4 tw-h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                            </button>
                        )}
                    </div>
                    {isLinked && (
                        <span className="tw-flex tw-items-baseline tw-gap-1.5 tw-mt-1" title={sourceLabel}>
                            <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-blue tw-shrink-0 tw-relative tw-top-px" />
                            <span className="tw-text-2xs tw-text-blue tw-break-words tw-min-w-0">
                                {$t('Linked to')}{' '}
                                {slotLinkState?.source_url ? (
                                    <a href={slotLinkState.source_url} className="tw-underline hover:tw-text-blue-75">
                                        {sourceLabel}
                                    </a>
                                ) : (
                                    <span className="tw-underline">{sourceLabel}</span>
                                )}
                            </span>
                        </span>
                    )}
                    {isBrokenLink && (
                        <span className="tw-text-xs tw-text-gray-400 tw-line-through tw-block tw-mt-0.5" title={sourceLabel}>
                            {sourceLabel}
                        </span>
                    )}
                </div>
            </td>
            <td>
                <div className="tw-flex tw-gap-4 tw-items-center">
                    <CreatableSelect
                        options={props.unitsObj}
                        isDisabled={props.expenseReadonly || !props.canBeEdited}
                        onChange={changeName}
                        value={{ label: props.name, value: props.name }}
                        name={`uncommitted_amount[${props.index}][name]`}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                        menuPlacement="top"
                    />
                    {/* A disabled select posts nothing, so a locked unit is posted as it stands. */}
                    {!props.canBeEdited && !props.expenseReadonly && (
                        <input type="hidden" name={`uncommitted_amount[${props.index}][name]`} value={props.name ?? ''} />
                    )}

                    {!props.expenseReadonly && !!props.canBeEdited && props.index != 0 && (
                        <div
                            className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-text-xs tw-cursor-pointer"
                            onClick={(e) => props.removeQuantity(props.index)}
                        >
                            <i className="far fa-trash-alt h5 mb-0"></i>
                            <small className="font-weight-600 w-100 d-block">{$t('Delete')}</small>
                        </div>
                    )}
                </div>
            </td>
        </Fragment>
    );
}
